import type { ClientAuthSummary } from '@/api/clients';

export const STATUS_TABS = ['All', 'Active', 'Pending', 'Waitlist', 'Inactive'] as const;
export type StatusTab = typeof STATUS_TABS[number];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function calcAge(dob: string | null): number | null {
    if (!dob) return null;
    const parts = dob.slice(0, 10).split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    const [y, m, d] = parts;
    const today = new Date();
    let age = today.getFullYear() - y;
    if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age--;
    return age;
}

export function fmtDob(dob: string | null): string {
    if (!dob) return '—';
    const parts = dob.slice(0, 10).split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return '—';
    const [y, m, d] = parts;
    return `${MONTHS[m - 1]} ${d}, ${y}`;
}

export type AuthExpiryWarning =
    | { kind: 'Expiring'; end: string }
    | { kind: 'Expired'; end: string };

/**
 * Returns a warning to render alongside the `Active` status badge when the
 * client's primary auth is about to expire or has already expired. `null`
 * when no warning applies (inactive client, no auth on file, auth still
 * has runway, or auth is pending). The status badge itself stays unchanged
 * — the caller renders this as a separate indicator to the right.
 */
export function authExpiryWarning(
    clientStatus: string,
    auth: ClientAuthSummary | null,
): AuthExpiryWarning | null {
    if (clientStatus !== 'Active') return null;
    if (!auth || !auth.end) return null;
    if (auth.status === 'Expiring') return { kind: 'Expiring', end: auth.end };
    if (auth.status === 'Expired')  return { kind: 'Expired',  end: auth.end };
    return null;
}

export function authRemainingHours(auth: ClientAuthSummary | null): number | null {
    if (!auth) return null;
    return Math.max(0, (auth.hours || 0) - (auth.used || 0));
}

export function authUsedPct(auth: ClientAuthSummary | null): number {
    if (!auth || !auth.hours) return 0;
    return Math.min(100, Math.round(((auth.used || 0) / auth.hours) * 100));
}
